import type {components} from '@rocket-ui/lk-api';

export type Task = components['schemas']['Task'];
export type ExtendedTask = components['schemas']['ExtendedTask'];

export function getTaskTitle(title: string | null | undefined, type: Task['type'], id: number | undefined): string {
	if (title) return title;
	if (id == null) return 'Без названия';

	const taskType = type as string | undefined;

	if (taskType === 'FrameworkAgreementTask' || taskType === 'framework') {
		return `Договор #${id}`;
	}

	if (taskType === 'OneTimeTask' || taskType === 'oneTime') {
		return `Заказ #${id}`;
	}

	return `Заказ #${id}`;
}

/** Заголовок на странице детальной таски — всегда по типу и id, без title из API. */
export function getTaskDetailTitle(type: Task['type'], id: number | undefined): string {
	return getTaskTitle(null, type, id);
}

export function getTaskCustomer(task: Task): string {
	return task.customerName ?? 'Не указан';
}

export function getTaskPrice(task: Task): string {
	if (!task.price) return '—';

	return `${new Intl.NumberFormat('ru-RU').format(Number(task.price)).replace(/\u00A0/g, ' ')} ₽`;
}

export function getTaskStatusLabel(status?: Task['status']): string {
	switch (status) {
		case 'draft':
			return 'Новый';
		case 'inwork':
			return 'В работе';
		case 'accepting':
			return 'На согласовании';
		case 'completed':
			return 'Завершен';
		case 'paying':
			return 'Ожидает оплату';
		case 'paid':
			return 'Оплачен';
		case 'error':
			return 'Ошибка оплаты';
		case 'canceled':
			return 'Отменен';
		case 'vacancy_in_progress':
			return 'Вакансия';
		case 'vacancy_stopped':
			return 'Вакансия остановлена';
		default:
			return '—';
	}
}

function pluralRu(n: number, one: string, few: string, many: string): string {
	const abs = Math.abs(Math.trunc(n)) % 100;
	const n1 = abs % 10;
	if (abs > 10 && abs < 20) return `${n} ${many}`;
	if (n1 > 1 && n1 < 5) return `${n} ${few}`;
	if (n1 === 1) return `${n} ${one}`;
	return `${n} ${many}`;
}

/** Короткая подпись срока для карточки списка (дни, часы или «вчера»). */
export function getTaskPeriodShortLabel(task: Task | ExtendedTask): string {
	const extended = task as ExtendedTask;

	if (extended.periodFrom && extended.periodTo) {
		const from = new Date(extended.periodFrom);
		const to = new Date(extended.periodTo);
		if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
			const diffMs = Math.max(0, to.getTime() - from.getTime());
			const hours = Math.round(diffMs / (3600 * 1000));
			if (hours > 0 && hours < 48) {
				return pluralRu(hours, 'час', 'часа', 'часов');
			}
			const days = Math.max(1, Math.round(diffMs / (86400 * 1000)));
			return pluralRu(days, 'день', 'дня', 'дней');
		}
	}

	if (extended.periodDuration != null && extended.periodDuration > 0) {
		return pluralRu(extended.periodDuration, 'день', 'дня', 'дней');
	}

	const ref = task.updatedAt ?? task.createdAt;
	if (ref) {
		const d = new Date(ref);
		if (!Number.isNaN(d.getTime())) {
			const now = new Date();
			const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
			const startRef = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
			const dayMs = 86400000;
			if (startToday - startRef === dayMs) return 'вчера';
		}
	}

	return '—';
}

export function getTaskServicesCount(task: Task | ExtendedTask): number {
	if ('servicesCount' in task && typeof task.servicesCount === 'number') {
		return task.servicesCount;
	}

	const services = (task as ExtendedTask).services;
	if (!services?.length) return 0;

	let total = 0;
	for (const s of services) {
		const raw = s.quantity as unknown;
		const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : NaN;
		total += n > 0 ? n : 1;
	}

	return total;
}

/** Инициалы заказчика для аватара (до 2 букв). */
export function getTaskCustomerInitials(customerName: string): string {
	const parts = customerName.trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return '';
	if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();

	return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
}

export type TaskStatusBadgeAlign = 'left' | 'center' | 'right';

/** Положение бейджа статуса на мобильной карточке (как в макете). */
export function getTaskStatusBadgeAlign(status?: Task['status']): TaskStatusBadgeAlign {
	switch (status) {
		case 'draft':
			return 'left';
		case 'inwork':
		case 'accepting':
		case 'vacancy_in_progress':
			return 'center';
		case 'paying':
			return 'right';
		default:
			return 'right';
	}
}
